import { z } from 'zod';

/** A present-but-empty variable is absent, as MathGaps/schools's `apps/web/src/lib/env.ts` reads one. */
const optional = z.preprocess(
  (value) => (value === '' ? undefined : value),
  z.string().min(1).optional(),
);

/**
 * What the build inlines (`admin-is-an-app` D1, D2). Dev's by default, so the app builds
 * with no `.env`, which is what a laptop and the tests want.
 *
 * A deploy never takes a default: `deploy/ENVIRONMENT.md` lists what the GitHub
 * environment must hold, and `ci.yml` refuses the build over a missing one. The defaults
 * below being dev's is a convenience, not the deploy's configuration — a production build
 * that fell back to them would serve production staff from dev's graph and say nothing.
 */
const schema = z.object({
  /*
   * The teacher's app, whose Worker is the one auth Worker: staff sign in there, the
   * admin calls its `/api/auth/*` and both tunnels with that session, and the View as
   * tool (`src/tools/view-as/`) opens a teacher's tab there.
   *
   * Dev is `tutero.dev`, which is what schools' Worker answers as — its `AUTH_BASE_URL`
   * (`deploy/web/wrangler.jsonc`), and so Better Auth's own base URL and the token's
   * `iss`. `ts.tutero.dev` was this default until 2026-11-02 and still answers, allowing
   * this origin, but it is the older host and not the one the Worker calls itself.
   *
   * It is NOT where a parent, student or tutor lives: those accounts are the tutoring
   * platform's, which is `VITE_PLATFORM_ORIGIN` below. On prod this origin is
   * `tutero.ai`, so a tutoring account sent here lands on Tutero AI (ISSUE-25625).
   */
  VITE_WEB_ORIGIN: z
    .string()
    .url()
    .default('https://tutero.dev')
    .transform((url) => new URL(url).origin),
  /*
   * The tutoring platform — where a parent, student or tutor's own account lives, and so
   * what Access Platform and a tutor's View Profile open. Flutter's `appHost`
   * (`admin-app/lib/src/constants.dart:145`): `app.tutero.dev` on dev, `app.tutero.com`
   * on prod.
   *
   * It is a different app from `VITE_WEB_ORIGIN` and not a second name for it. The
   * teacher's app answers neither contract: it has no `/tutors/<id>` route at all, and
   * its `/loading` is a 3.0 shim that redirects to `/` and drops the query string
   * (`schools-ts apps/web/src/routes/loading.tsx`).
   */
  VITE_PLATFORM_ORIGIN: z
    .string()
    .url()
    .default('https://app.tutero.dev')
    .transform((url) => new URL(url).origin),
  /*
   * Where Better Auth's `/api/auth/*` is, when that is not the teacher's app's own origin
   * (`staff-sign-in-here` D2). `vite.config.ts` defaults it to the dev server's origin under
   * `DEV_API_PROXY`, so the proxied `/api` is what the client dials and the session cookie
   * stays host-only. Sign-out and the View as tool keep naming `VITE_WEB_ORIGIN`.
   */
  VITE_AUTH_ORIGIN: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z
      .string()
      .url()
      .transform((url) => new URL(url).origin)
      .optional(),
  ),
  /*
   * The session bridge (`the-session-crosses-to-tutero-com` D10): set, two things are true
   * of this build and the Worker that serves it — this host answers its own `/api/auth/*`
   * (`deploy/wrangler.jsonc` forwards them to the one auth Worker), and the auth Worker's
   * `AUTH_CROSS_DOMAIN_PAIRS` names this origin beside the teacher's app's.
   *
   * So the auth client dials this origin rather than `VITE_WEB_ORIGIN`, a signed-out
   * arrival asks the partner for the session once before drawing a form nobody may need
   * (`src/lib/bridge.ts`), and signing out clears both hosts.
   *
   * Off by default, which is a laptop, the tests, and every build made before the pairing
   * names this host: the admin signs in against `VITE_WEB_ORIGIN` over credentialed CORS,
   * as it does today. One variable and not two, because the two facts arrive together —
   * the forwarding and the pairing are the same deploy, and a build that bounced to
   * `/api/auth/cross/start` on a host that does not forward it would be served index.html.
   */
  VITE_SESSION_BRIDGE: z.preprocess(
    (value) => value === '1' || value === 'true',
    z.boolean(),
  ),
  /*
   * The graph: the Cosmo router, which every tool's operation goes through (D2).
   * `api.tutero.dev`, not `api-ts.tutero.dev`: both reach the same router, but only the first
   * allows a browser (verified 2026-10-27 — `api-ts.tutero.dev` sends no CORS headers), and only
   * its edge honours `X-Impersonating-Uid` for a Better Auth token (`tutors-on-the-graph` D16).
   */
  VITE_GRAPHQL_ENDPOINT: z.string().url().default('https://api.tutero.dev'),
  /*
   * agent-chat's API on its own host (`agents.<domain>`), whose abyme door the agent pages enter: `POST /v1/abyme/session` swaps the
   * staff token for a one-time link to the agent's host (`am-agent.<domain>`, `cs-agent.<domain>`), which the page frames. Not the graph:
   * the door is a login hand-off, not data (a DISCUSS-FIRST against A2, see the AM page).
   * Optional, with no default: unset, the agent pages are unavailable rather than calling dev's
   * host from a production build.
   */
  VITE_AGENTS_URL: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z
      .string()
      .url()
      .transform((url) => url.replace(/\/+$/, ''))
      .optional(),
  ),
  /*
   * Which build this is. Only a production build names `prod`, and no production build
   * exists yet, so every build reads `dev`. It no longer decides which regions the picker
   * offers — `the-region-picker-offers-both` found D10's premise false and offers both in
   * every build — so what is left is the telemetry's environment.
   *
   * The default is for a laptop and the tests. A deploy states it: `deploy/ENVIRONMENT.md`
   * is the list `ci.yml` refuses a build over, so a deploy cannot fall back to this.
   */
  VITE_APP_ENV: z.enum(['dev', 'prod']).default('dev'),
  VITE_SENTRY_DSN: optional,
  VITE_SENTRY_ENVIRONMENT: optional,
  VITE_MIXPANEL_TOKEN: optional,
  VITE_APP_VERSION: optional,
  /*
   * A local dev convenience: when `1`, the nav shows every tool regardless of the
   * session's grants, so a reviewer can walk the whole admin on one account. The client
   * gate is convenience only — each backend still refuses an operation the caller's
   * grants do not hold (A4) — so a tool whose grant the account lacks draws but its data
   * is refused. Never set in a deployed build.
   */
  VITE_DEV_PERMS: z.preprocess(
    (value) => value === '1' || value === 'true',
    z.boolean(),
  ),
});

export const env = schema.parse(import.meta.env);
